// The scoreboard's line (projects/investing spec §5.4, D9, D15; walkthrough
// 11): the account against the same money held in the benchmark (the
// "shadow") at EVERY snapshot, worked out here — GET scoreboard/ gives the
// latest snapshot's figures only, and PR 4 added no series.
//
// It follows backend/investing/scoreboard.py exactly, so the line's last
// point is the server's own scoreboard to the cent (pinned against the
// backend's hand-computed fixture in scoreboard.test.js, and checked on the
// page against GET scoreboard/):
//
// - a record counts at a snapshot when it's dated on or before the
//   snapshot's day in New York (`marketDay`);
// - each deposit buys amount ÷ the benchmark's close that day, rounded half
//   up to 8 places (a withdrawal sells the same way); each distribution,
//   dated by its ex-dividend date, adds units × per share ÷ that day's
//   close, rounded the same, on the units held before that day's
//   contributions;
// - the shadow's value is units × the snapshot's benchmark price, half up
//   to the cent; the account's is the snapshot's total value.
//
// Every figure is exact: decimal strings read as BigInt numerators over a
// power of ten, each step one exact division rounded half away from zero —
// what Python's Decimal does at the 200 digits the server works in. Units
// are whole 1/10^8ths; money is whole cents.

import { divideRounded } from "./money";

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const UNIT_PLACES = 8;
const MARKET_TIME_ZONE = "America/New_York";

/** A decimal string as `{n, scale}` — exactly n / 10^scale — or null. */
function exact(raw) {
  const match = typeof raw === "string" ? DECIMAL.exec(raw) : null;
  if (!match) return null;
  const fraction = match[3] ?? "";
  const n = BigInt(`${match[2]}${fraction}`);
  return { n: match[1] ? -n : n, scale: fraction.length };
}

/** A price the math can divide by: a decimal string above zero, or null. */
function price(raw) {
  const value = exact(raw);
  return value && value.n > 0n ? value : null;
}

const pow10 = (places) => 10n ** BigInt(places);

let marketDays = null;

/**
 * An instant's day in New York ("YYYY-MM-DD") — the market's day, as the
 * server's `through_of` takes it. null for anything that isn't an instant.
 */
export function marketDay(iso) {
  if (typeof iso !== "string" || iso === "") return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  marketDays ??= new Intl.DateTimeFormat("en-US", {
    timeZone: MARKET_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const part = Object.fromEntries(marketDays.formatToParts(at).map(({ type, value }) => [type, value]));
  return `${part.year}-${part.month}-${part.day}`;
}

/**
 * The records the math reads, checked once: `{flows, payouts}` with exact
 * figures, or null when any of them can't be read (a bad decimal, a price
 * of 0 or less, a date that isn't YYYY-MM-DD) — the line then has no
 * figure, never a made-up one.
 */
function records(contributions, distributions) {
  const flows = [];
  for (const row of contributions ?? []) {
    const amount = exact(row?.amount);
    const close = price(row?.benchmark_price);
    if (!amount || !close || !DAY.test(row?.date ?? "")) return null;
    flows.push({ date: row.date, deposit: row.kind === "deposit", amount, close });
  }
  const payouts = [];
  for (const row of distributions ?? []) {
    const perShare = exact(row?.amount_per_share);
    const close = price(row?.benchmark_price);
    if (!perShare || !close || !DAY.test(row?.date ?? "")) return null;
    payouts.push({ date: row.date, perShare, close });
  }
  return { flows, payouts };
}

/** amount ÷ close in whole units (1/10^8), half up. */
function bought({ amount, close }) {
  return divideRounded(amount.n * pow10(UNIT_PLACES + close.scale), close.n * pow10(amount.scale));
}

/** The units on `through`: the records in date order, a day's distribution first. */
function unitsOn({ flows, payouts }, through) {
  const events = [
    ...payouts.filter((p) => p.date <= through).map((p) => ({ date: p.date, order: 0, payout: p })),
    ...flows.filter((f) => f.date <= through).map((f) => ({ date: f.date, order: 1, flow: f })),
  ].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.order - b.order));
  let units = 0n;
  for (const event of events) {
    if (event.payout) {
      const { perShare, close } = event.payout;
      // units × per share ÷ close, still in 1/10^8ths
      units += divideRounded(units * perShare.n * pow10(close.scale), close.n * pow10(perShare.scale));
    } else if (event.flow.deposit) {
      units += bought(event.flow);
    } else {
      units -= bought(event.flow);
    }
  }
  return units;
}

/**
 * The shadow's units (whole 1/10^8ths, BigInt) on `through` ("YYYY-MM-DD",
 * a snapshot's day in New York), from the API's contribution and
 * distribution rows in any order. null when a row can't be read.
 */
export function shadowUnits(contributions, distributions, through) {
  const read = records(contributions, distributions);
  return read ? unitsOn(read, through) : null;
}

/** A decimal as whole cents, half up. */
const cents = (value) => divideRounded(value.n * 100n, pow10(value.scale));

/**
 * The account and the shadow at every snapshot (the API's snapshot rows,
 * any order), oldest first — by `as_of`, then id, as the API orders them.
 * Each point: `{id, asOf, through, units, account, shadow, net, difference}`
 * — units in 1/10^8ths, money in whole cents (BigInt). [] before the first
 * snapshot; null when any row can't be read.
 */
export function scoreboardSeries(snapshots, contributions, distributions) {
  const read = records(contributions, distributions);
  if (!read) return null;
  const rows = [];
  for (const row of snapshots ?? []) {
    const through = marketDay(row?.as_of);
    const total = exact(row?.total_value);
    const benchmark = exact(row?.benchmark_price);
    if (!through || !total || !benchmark) return null;
    rows.push({ row, at: new Date(row.as_of).getTime(), through, total, benchmark });
  }
  rows.sort((a, b) => a.at - b.at || a.row.id - b.row.id);
  return rows.map(({ row, through, total, benchmark }) => {
    const units = unitsOn(read, through);
    // units/10^8 × price/10^scale, in cents: × 100 ÷ 10^(8 + scale)
    const shadow = divideRounded(units * benchmark.n, pow10(UNIT_PLACES - 2 + benchmark.scale));
    const account = cents(total);
    let net = 0n;
    for (const flow of read.flows) {
      if (flow.date > through) continue;
      net += flow.deposit ? cents(flow.amount) : -cents(flow.amount);
    }
    return { id: row.id, asOf: row.as_of, through, units, account, shadow, net, difference: account - shadow };
  });
}
